'use client';

import { useCallback, useState } from 'react';
import Link from 'next/link';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Eye, EyeOff, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { useCaptcha } from '@kit/auth/captcha/client';
import {
  AuthErrorAlert,
  AuthRecoveryHint,
  isInvalidCredentialsError,
} from '@kit/auth/components';
import { useLastAuthMethod } from '@kit/auth/hooks/use-last-auth-method';
import { PasswordSignInSchema } from '@kit/auth/schema';
import { useSignInWithOtp } from '@kit/supabase/hooks/use-sign-in-with-otp';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { useEmailPasswordSignIn } from '../_lib/hooks/use-email-password-sign-in';

/**
 * Single email-based sign-in form: ONE email input shared by two actions —
 * "Sign in with Email" (email + password) and "Send me a magic link" (email
 * only, via `useSignInWithOtp`). The magic link reuses the same email input as
 * the password form (no duplicate field).
 *
 * The form schema requires email + password (for the password sign-in), but the
 * magic-link button validates ONLY the email, so it works with the password
 * left blank.
 */
export function MeteorEmailAuthForm({
  onSignIn,
  redirectUrl,
  showMagicLink,
  oauthPaths,
}: {
  onSignIn?: (userId?: string) => unknown;
  redirectUrl: string;
  showMagicLink: boolean;
  /**
   * Callback + return paths for the directed recovery hint's one-click
   * provider button, shown when the typed email belongs to an OAuth-only
   * account (e.g. a Google account being signed into with a password).
   */
  oauthPaths?: { callback: string; returnPath: string };
}) {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const captcha = useCaptcha();
  const { recordAuthMethod } = useLastAuthMethod();

  const signInMutation = useEmailPasswordSignIn();
  const magicLinkMutation = useSignInWithOtp();

  const form = useForm({
    resolver: zodResolver(PasswordSignInSchema),
    defaultValues: { email: '', password: '' },
  });

  const isPasswordLoading = signInMutation.isPending;
  const isRedirecting = signInMutation.isSuccess;
  const isMagicLoading = magicLinkMutation.isPending;
  const captchaLoading = !captcha.isReady;

  const onSubmit = useCallback(
    async (credentials: z.infer<typeof PasswordSignInSchema>) => {
      setSubmittedEmail(credentials.email);

      try {
        await signInMutation.mutateAsync({
          email: credentials.email,
          password: credentials.password,
        });

        recordAuthMethod('password', { email: credentials.email });
        onSignIn?.();
      } catch {
        // Failure is surfaced below via signInMutation.error (AuthErrorAlert +
        // AuthRecoveryHint + the password-setup notice). Meteor-era passwords
        // are no longer accepted (BAL-1048): a user migrated from the old app
        // who has never set a password here is emailed a set-password link by
        // the server action, and the notice tells every failed sign-in so
        // without revealing whether the account exists.
      } finally {
        captcha.reset();
      }
    },
    [signInMutation, recordAuthMethod, onSignIn, captcha],
  );

  // Magic link reuses the email field — validate email only (password is not
  // required for a magic link).
  const onSendMagicLink = useCallback(async () => {
    const isEmailValid = await form.trigger('email');
    if (!isEmailValid) return;

    const email = form.getValues('email');
    const emailRedirectTo = new URL(redirectUrl).href;

    const promise = async () => {
      await magicLinkMutation.mutateAsync({
        email,
        options: {
          emailRedirectTo,
          captchaToken: captcha.token,
          shouldCreateUser: false,
        },
      });

      recordAuthMethod('magic_link', { email });
    };

    toast.promise(promise, {
      loading: t('sendingEmailLink'),
      success: t('sendLinkSuccessToast'),
      error: t('errors.linkTitle'),
    });

    captcha.reset();
  }, [form, redirectUrl, magicLinkMutation, captcha, recordAuthMethod, t]);

  return (
    <div className="flex w-full flex-col gap-y-4">
      <AuthErrorAlert error={signInMutation.error} />

      {/* Shown for EVERY invalid-credentials failure, so it reveals nothing
          about the account: the server action emails a set-password link
          only when the address belongs to a Meteor-migrated user who has
          never set a password here (BAL-1048). */}
      <If condition={isInvalidCredentialsError(signInMutation.error)}>
        <Alert data-test="meteor-password-setup-notice">
          <AlertTitle>
            <Trans i18nKey="auth:meteorPasswordSetupNoticeTitle" />
          </AlertTitle>
          <AlertDescription>
            <Trans i18nKey="auth:meteorPasswordSetupNoticeBody" />
          </AlertDescription>
        </Alert>
      </If>

      <If
        condition={
          isInvalidCredentialsError(signInMutation.error) &&
          submittedEmail.length > 0
        }
      >
        <AuthRecoveryHint
          email={submittedEmail}
          context={'sign-in'}
          oauthPaths={oauthPaths}
        />
      </If>

      <Form {...form}>
        <form
          className="flex w-full flex-col gap-y-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {captcha.field}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="common:emailAddress" />
                </FormLabel>

                <FormControl>
                  <Input
                    data-test="email-input"
                    required
                    type="email"
                    autoFocus
                    placeholder={t('auth:emailPlaceholder')}
                    {...field}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Trans i18nKey="common:password" />
                </FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      data-test="password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth:passwordPlaceholder')}
                      className="pr-10"
                      {...field}
                    />

                    <Button
                      type="button"
                      variant="ghost"
                      tabIndex={-1}
                      aria-label={t(
                        showPassword
                          ? 'auth:hidePassword'
                          : 'auth:showPassword',
                      )}
                      className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center pr-3"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>

                <FormMessage />

                <div className="flex justify-end">
                  <Link
                    href="/auth/password-reset"
                    className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
                  >
                    <Trans i18nKey="auth:passwordForgottenQuestion" />
                  </Link>
                </div>
              </FormItem>
            )}
          />

          <Button
            data-test="auth-submit-button"
            className="group w-full"
            type="submit"
            disabled={isPasswordLoading || isRedirecting || captchaLoading}
          >
            <If condition={isRedirecting}>
              <span className="animate-in fade-in slide-in-from-bottom-24">
                <Trans i18nKey="auth:redirecting" />
              </span>
            </If>

            <If condition={isPasswordLoading}>
              <span className="animate-in fade-in slide-in-from-bottom-24">
                <Trans i18nKey="auth:signingIn" />
              </span>
            </If>

            <If
              condition={
                !isRedirecting && !isPasswordLoading && !captchaLoading
              }
            >
              <span className="animate-in fade-in slide-in-from-bottom-24 flex items-center">
                <Trans i18nKey="auth:signInWithEmail" />
                <ArrowRight className="animate-in zoom-in slide-in-from-left-2 fill-mode-both h-4 delay-500 duration-500" />
              </span>
            </If>
          </Button>

          {/* Magic link — same email input, no password required. A quiet
              text link (not a full-width button) so it doesn't compete with
              the primary "Sign in" CTA; matches the Copy.ai sign-in pattern
              of folding the password-free option in subtly. */}
          <If condition={showMagicLink}>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-muted-foreground hover:text-foreground h-auto gap-1.5"
                data-test="send-magic-link-button"
                disabled={isMagicLoading || captchaLoading}
                onClick={onSendMagicLink}
              >
                <Mail className="h-3.5 w-3.5" />
                <If condition={isMagicLoading}>
                  <Trans i18nKey="auth:sendingEmailLink" />
                </If>
                <If condition={!isMagicLoading}>
                  <Trans i18nKey="auth:sendMagicLink" />
                </If>
              </Button>
            </div>
          </If>
        </form>
      </Form>
    </div>
  );
}
